import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, Ban, RotateCcw, UserX, Users, Trash2, MapPin, Mail, MailOpen, BadgeCheck, Check } from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import VerifyReviewCard from "../components/VerifyReviewCard";
import { toast } from "sonner";

export default function AdminPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [log, setLog] = useState([]);
  const [banned, setBanned] = useState([]);
  const [guardians, setGuardians] = useState([]);
  const [actions, setActions] = useState([]);
  const [suspensions, setSuspensions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [ngroups, setNgroups] = useState([]);
  const [inbox, setInbox] = useState({ messages: [], unread: 0 });
  const [verifReqs, setVerifReqs] = useState([]);
  const [verifSections, setVerifSections] = useState([]);
  const [newSecLabel, setNewSecLabel] = useState("");
  const [newSecRequired, setNewSecRequired] = useState(false);
  const [christmasOn, setChristmasOn] = useState(false);

  const load = () => {
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/admin/moderation-log").then((r) => setLog(r.data)).catch(() => {});
    api.get("/admin/banned").then((r) => setBanned(r.data)).catch(() => {});
    api.get("/admin/guardians").then((r) => setGuardians(r.data)).catch(() => {});
    api.get("/admin/guardian-actions").then((r) => setActions(r.data)).catch(() => {});
    api.get("/admin/suspensions").then((r) => setSuspensions(r.data)).catch(() => {});
    api.get("/admin/guardian-applications").then((r) => setApplications(r.data)).catch(() => {});
    api.get("/admin/neighbor-groups").then((r) => setNgroups(r.data)).catch(() => {});
    api.get("/admin/contact").then((r) => setInbox(r.data)).catch(() => {});
    api.get("/admin/verification-requests").then((r) => setVerifReqs(r.data)).catch(() => {});
    api.get("/admin/verification-sections").then((r) => setVerifSections(r.data)).catch(() => {});
    api.get("/settings/theme").then((r) => setChristmasOn(!!r.data.christmas)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user && user.role !== "admin") {
    return <div className="max-w-2xl mx-auto px-4 py-20 text-center"><p className="text-muted-foreground">This area is for moderators only.</p></div>;
  }

  const ban = async (id) => { try { await api.post(`/admin/ban/${id}`); toast.success("User permanently removed"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const unban = async (id) => { try { await api.post(`/admin/unban/${id}`); toast.success("User restored"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const revokeGuardian = async (id) => { try { await api.delete(`/admin/guardian/${id}`); toast.success("Guardian removed"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const unsuspend = async (id) => { try { await api.post(`/moderate/unsuspend/${id}`); toast.success("Suspension lifted"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const decideApp = async (id, decision) => { try { await api.post(`/admin/guardian-applications/${id}/${decision}`); toast.success(decision === "approve" ? "Approved — they're a guardian 🛡️" : "Application declined"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const deleteGroup = async (id) => { if (!window.confirm("Delete this group and all its initiatives & gift cards? This can't be undone.")) return; try { await api.delete(`/admin/neighbor-groups/${id}`); toast.success("Group deleted"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleReadMsg = async (id) => { try { await api.post(`/admin/contact/${id}/read`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const deleteMsg = async (id) => { if (!window.confirm("Delete this message?")) return; try { await api.delete(`/admin/contact/${id}`); toast.success("Message deleted"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const approveVerif = async (id, body) => { try { await api.post(`/admin/verification-requests/${id}/approve`, body || {}); toast.success(body?.protected ? "Approved — Under Our Wing 🤍" : "Member verified & trusted ✅"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const declineVerif = async (id) => { try { await api.post(`/admin/verification-requests/${id}/decline`); toast.success("Request declined"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const openDoc = async (docUrl) => { try { const path = docUrl.replace(/^\/api\/files\//, ""); const { data } = await api.get("/admin/files/sign", { params: { path } }); window.open(fileUrl(data.url), "_blank", "noopener"); } catch (e) { toast.error(apiErr(e)); } };
  const addSection = async () => { if (!newSecLabel.trim()) return; try { await api.post("/admin/verification-sections", { label: newSecLabel, required: newSecRequired }); setNewSecLabel(""); setNewSecRequired(false); toast.success("Section added"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleSecRequired = async (s) => { try { await api.put(`/admin/verification-sections/${s.id}`, { label: s.label, required: !s.required }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const deleteSection = async (id) => { if (!window.confirm("Remove this document section?")) return; try { await api.delete(`/admin/verification-sections/${id}`); toast.success("Section removed"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleChristmas = async () => { try { const { data } = await api.post("/admin/settings/theme", { christmas: !christmasOn }); setChristmasOn(data.christmas); toast.success(data.christmas ? "Christmas theme is LIVE 🎄" : "Christmas theme turned off"); } catch (e) { toast.error(apiErr(e)); } };

  const sevColor = (s) => s === "severe" ? "text-destructive bg-destructive/10" : "text-amber-600 bg-amber-500/10";

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <p className="font-mono text-xs uppercase tracking-widest text-copper flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Moderation</p>
      <h1 className="font-serif text-3xl font-bold text-bronze mb-1">Safety center</h1>
      <p className="text-muted-foreground mb-6">Zero tolerance for harassment. Review reports, auto-flagged messages and banned users.</p>

      <Tabs defaultValue="reports">
        <TabsList className="rounded-full bg-secondary p-1 flex-wrap h-auto">
          <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-full">Reports ({reports.length})</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-modlog" className="rounded-full">Flagged ({log.length})</TabsTrigger>
          <TabsTrigger value="suspensions" data-testid="tab-suspensions" className="rounded-full">Cool-downs ({suspensions.length})</TabsTrigger>
          <TabsTrigger value="guardians" data-testid="tab-guardians" className="rounded-full">Guardians ({guardians.length})</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-applications" className="rounded-full">Applications ({applications.length})</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions" className="rounded-full">Guardian log ({actions.length})</TabsTrigger>
          <TabsTrigger value="neighbors" data-testid="tab-neighbors" className="rounded-full">Neighbors ({ngroups.length})</TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox" className="rounded-full">Inbox ({inbox.unread})</TabsTrigger>
          <TabsTrigger value="verify" data-testid="tab-verify" className="rounded-full">Verify ({verifReqs.length})</TabsTrigger>
          <TabsTrigger value="campaign" data-testid="tab-campaign" className="rounded-full">Campaign</TabsTrigger>
          <TabsTrigger value="banned" data-testid="tab-banned" className="rounded-full">Banned ({banned.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="mt-5">
          {reports.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No reports. The community is peaceful 💛</div>
          ) : (
            <div className="space-y-3" data-testid="reports-list">
              {reports.map((r) => (
                <div key={r.id} className="bg-card rounded-2xl border border-border p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm">{r.target_user_id ? <>Report on user <Link className="text-copper" to={`/u/${r.target_user_id}`}>{r.target_user_id}</Link></> : `Report on post ${r.target_post_id}`}</p>
                      {r.chat_text && <p className="text-xs text-foreground/60 mt-1 italic bg-secondary/60 rounded-lg px-2 py-1">"{r.chat_text}"</p>}
                      <p className="text-foreground/80 mt-1">{r.reason}</p>
                      <p className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleString()}</p>
                    </div>
                    {r.target_user_id && (
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" onClick={() => ban(r.target_user_id)} className="bg-destructive hover:bg-destructive/90 text-white rounded-full"><Ban className="w-4 h-4 mr-1" /> Ban</Button>
                        <Button size="sm" variant="outline" onClick={() => unban(r.target_user_id)} className="rounded-full"><RotateCcw className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="log" className="mt-5">
          {log.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No flagged messages. 💛</div>
          ) : (
            <div className="space-y-2" data-testid="modlog-list">
              {log.map((m) => (
                <div key={m.id} className="bg-card rounded-2xl border border-border p-3.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-full ${sevColor(m.severity)}`}>{m.severity}</span>
                      <Link className="text-sm font-medium text-copper" to={`/u/${m.user_id}`}>{m.name}</Link>
                      <span className="text-xs text-muted-foreground">· {m.context}</span>
                    </div>
                    <p className="text-sm text-foreground/70 mt-1 italic break-words">"{m.text}"</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{new Date(m.created_at).toLocaleString()}</p>
                  </div>
                  <Button size="sm" onClick={() => ban(m.user_id)} className="bg-destructive hover:bg-destructive/90 text-white rounded-full shrink-0"><Ban className="w-4 h-4 mr-1" /> Ban</Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="suspensions" className="mt-5">
          {suspensions.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No active cool-downs. 💛</div>
          ) : (
            <div className="space-y-2" data-testid="suspensions-list">
              {suspensions.map((s) => (
                <div key={s.user_id} className="bg-card rounded-2xl border border-border p-3.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <img src={fileUrl(s.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${s.user_id}`} alt="" className="w-9 h-9 rounded-full bg-secondary shrink-0" />
                    <div className="min-w-0">
                      <Link to={`/u/${s.user_id}`} className="text-sm font-medium truncate hover:text-copper flex items-center gap-1.5">{s.name}
                        {s.ban_recommended && <span data-testid={`ban-rec-${s.user_id}`} className="text-[9px] font-semibold text-destructive bg-destructive/10 px-1.5 py-0.5 rounded-full">⚠ RECOMMEND BAN</span>}
                      </Link>
                      <p className="text-xs text-muted-foreground truncate">until {new Date(s.suspended_until).toLocaleString()} · {s.reason || "—"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => unsuspend(s.user_id)} className="rounded-full">Lift</Button>
                    <Button size="sm" onClick={() => ban(s.user_id)} className="bg-destructive hover:bg-destructive/90 text-white rounded-full"><Ban className="w-4 h-4 mr-1" /> Ban</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="guardians" className="mt-5">
          <p className="text-sm text-muted-foreground mb-3">Grant guardian powers from any member's profile using the "Make guardian" button. Guardians can delete rude content and issue 24h cool-downs; misuse means removal.</p>
          {guardians.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No guardians yet.</div>
          ) : (
            <div className="space-y-2" data-testid="guardians-list">
              {guardians.map((g) => (
                <div key={g.user_id} className="bg-card rounded-2xl border border-border p-3.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <img src={fileUrl(g.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${g.user_id}`} alt="" className="w-9 h-9 rounded-full bg-secondary shrink-0" />
                    <div className="min-w-0">
                      <Link to={`/u/${g.user_id}`} className="text-sm font-medium truncate hover:text-copper flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-copper" /> {g.name}</Link>
                      <p className="text-xs text-muted-foreground truncate">{g.account_type} · {g.email}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => revokeGuardian(g.user_id)} className="rounded-full shrink-0">Remove</Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="applications" className="mt-5">
          {applications.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No guardian applications right now.</div>
          ) : (
            <div className="space-y-2" data-testid="applications-list">
              {applications.map((a) => (
                <div key={a.id} className="bg-card rounded-2xl border border-border p-3.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <img src={fileUrl(a.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${a.user_id}`} alt="" className="w-9 h-9 rounded-full bg-secondary shrink-0" />
                    <div className="min-w-0">
                      <Link to={`/u/${a.user_id}`} className="text-sm font-medium truncate hover:text-copper">{a.name}</Link>
                      <p className="text-xs text-muted-foreground break-words">{a.reason || "No message"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" data-testid={`approve-app-${a.id}`} onClick={() => decideApp(a.id, "approve")} className="bg-sage hover:bg-sage/90 text-white rounded-full">Approve</Button>
                    <Button size="sm" data-testid={`decline-app-${a.id}`} variant="outline" onClick={() => decideApp(a.id, "reject")} className="rounded-full">Decline</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>


        <TabsContent value="actions" className="mt-5">
          {actions.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No guardian actions yet.</div>
          ) : (
            <div className="space-y-2" data-testid="actions-list">
              {actions.map((a) => (
                <div key={a.id} className="bg-card rounded-2xl border border-border p-3 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full ${a.actor_role === "admin" ? "bg-bronze/15 text-bronze" : "bg-copper/15 text-copper"}`}>{a.actor_role}</span>
                    <span className="font-medium">{a.actor_name}</span>
                    <span className="text-muted-foreground">→ {a.action.replace("_", " ")}</span>
                    {a.target_user_id && <Link to={`/u/${a.target_user_id}`} className="text-copper text-xs">view target</Link>}
                  </div>
                  {a.detail && <p className="text-xs text-foreground/70 mt-1 italic break-words">"{a.detail}"</p>}
                  <p className="text-[11px] text-muted-foreground mt-0.5">{new Date(a.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="neighbors" className="mt-5">
          <p className="text-sm text-muted-foreground mb-3">Full oversight of every private Neighbors group — funds, initiatives and gift cards. Delete anything unsafe.</p>
          {ngroups.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No neighbor groups yet.</div>
          ) : (
            <div className="space-y-2" data-testid="neighbor-groups-list">
              {ngroups.map((g) => (
                <div key={g.group_id} className="bg-card rounded-2xl border border-border p-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-bronze truncate flex items-center gap-1.5"><Users className="w-4 h-4 text-copper" /> {g.name}</p>
                      <p className="text-xs text-muted-foreground break-words mt-0.5">
                        {g.area && <span className="inline-flex items-center gap-1 mr-2"><MapPin className="w-3 h-3" /> {g.area}</span>}
                        by {g.creator?.name || "—"} · code <span className="font-mono">{g.join_code}</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{g.member_count} members · ${(g.fund_balance || 0).toFixed(2)} pot · {g.initiative_count} initiatives · {g.giftcard_count} gift cards</p>
                    </div>
                    <Button size="sm" data-testid={`delete-group-${g.group_id}`} onClick={() => deleteGroup(g.group_id)} variant="outline" className="rounded-full shrink-0 text-destructive border-destructive/40"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="inbox" className="mt-5">
          <p className="text-sm text-muted-foreground mb-3">Messages sent through the Contact page. Reply from your email client to the sender's address.</p>
          {inbox.messages.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No messages yet. 💛</div>
          ) : (
            <div className="space-y-2" data-testid="inbox-list">
              {inbox.messages.map((m) => (
                <div key={m.id} data-testid={`inbox-msg-${m.id}`}
                  className={`rounded-2xl border p-4 ${m.read ? "bg-card border-border" : "bg-copper/5 border-copper/30"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-bronze truncate flex items-center gap-1.5">
                        {!m.read && <span className="w-2 h-2 rounded-full bg-copper shrink-0" />}
                        {m.subject || "(no subject)"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 break-words">
                        from <span className="font-medium text-foreground/80">{m.name}</span> ·{" "}
                        <a href={`mailto:${m.email}`} className="text-copper underline">{m.email}</a> · {new Date(m.created_at).toLocaleString()}
                      </p>
                      <p className="text-sm text-foreground/85 mt-2 whitespace-pre-wrap break-words bg-secondary/40 rounded-xl px-3 py-2">{m.message}</p>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button size="sm" variant="outline" data-testid={`inbox-read-${m.id}`} onClick={() => toggleReadMsg(m.id)} className="rounded-full" title={m.read ? "Mark as unread" : "Mark as read"}>
                        {m.read ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`inbox-delete-${m.id}`} onClick={() => deleteMsg(m.id)} className="rounded-full text-destructive border-destructive/40"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="verify" className="mt-5">
          <div className="rounded-2xl border border-border bg-card p-4 mb-4" data-testid="verif-sections-manager">
            <p className="font-serif text-lg text-bronze mb-1">Verification document sections</p>
            <p className="text-sm text-muted-foreground mb-3">Add your own sections (e.g. "Proof of address"). Members will see an upload box for each.</p>
            <div className="space-y-2 mb-3">
              {verifSections.map((s) => (
                <div key={s.id} data-testid={`verif-section-row-${s.id}`} className="flex items-center justify-between gap-2 bg-secondary/40 rounded-xl px-3 py-2">
                  <span className="text-sm text-foreground/85">{s.label}</span>
                  <div className="flex items-center gap-3 shrink-0">
                    <button data-testid={`section-required-${s.id}`} onClick={() => toggleSecRequired(s)}
                      className={`text-xs font-medium px-2 py-0.5 rounded-full border ${s.required ? "bg-copper/15 text-copper border-copper/30" : "text-muted-foreground border-border"}`}>
                      {s.required ? "Required" : "Optional"}
                    </button>
                    <button data-testid={`section-delete-${s.id}`} onClick={() => deleteSection(s.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input data-testid="new-section-label" value={newSecLabel} onChange={(e) => setNewSecLabel(e.target.value)}
                placeholder="New section label…" className="flex-1 rounded-xl border border-border px-3 py-2 text-sm bg-white" />
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                <input type="checkbox" data-testid="new-section-required" checked={newSecRequired} onChange={(e) => setNewSecRequired(e.target.checked)} /> Required
              </label>
              <Button size="sm" data-testid="add-section-btn" onClick={addSection} className="rounded-full bg-copper hover:bg-copper/90 text-white">Add</Button>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-3">Members requesting a Verified badge. Approving grants the badge instantly and notifies them.</p>
          {verifReqs.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">No pending verification requests. 💛</div>
          ) : (
            <div className="space-y-2" data-testid="verify-list">
              {verifReqs.map((r) => (
                <VerifyReviewCard key={r.id} req={r} onApprove={approveVerif} onDecline={declineVerif} onOpenDoc={openDoc} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="campaign" className="mt-5">
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-serif text-xl font-semibold text-bronze flex items-center gap-2">🎄 Christmas campaign theme</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Turn on tasteful holiday touches across the whole platform — gentle falling snow and a twinkling lights garland under the header. Flip it off anytime after the season.</p>
            <div className="flex items-center justify-between bg-secondary/40 rounded-2xl p-4">
              <div>
                <p className="font-medium text-bronze">Holiday theme</p>
                <p className="text-xs text-muted-foreground">{christmasOn ? "Live for everyone right now 🎄" : "Currently off"}</p>
              </div>
              <Button data-testid="christmas-toggle" onClick={toggleChristmas}
                className={`rounded-full ${christmasOn ? "bg-sage hover:bg-sage/90" : "bg-copper hover:bg-copper/90"} text-white`}>
                {christmasOn ? "Turn OFF" : "Turn ON 🎄"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Tip: pair this with the <Link to="/sponsor" className="text-copper underline">Sponsor a Family</Link> page for your holiday drive.</p>
          </div>
        </TabsContent>

        <TabsContent value="banned" className="mt-5">
          {banned.length === 0 ? (
            <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground">Nobody is banned. 💛</div>
          ) : (
            <div className="space-y-2" data-testid="banned-list">
              {banned.map((b) => (
                <div key={b.user_id} className="bg-card rounded-2xl border border-border p-3.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <img src={fileUrl(b.avatar_url) || `https://api.dicebear.com/7.x/lorelei/svg?seed=${b.user_id}`} alt="" className="w-9 h-9 rounded-full bg-secondary shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate flex items-center gap-1"><UserX className="w-3.5 h-3.5 text-destructive" /> {b.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{b.email} · {b.reason}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => unban(b.user_id)} className="rounded-full shrink-0"><RotateCcw className="w-4 h-4 mr-1" /> Restore</Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
